import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import type { MonetizationType, PresentationType } from '@/@types/graphql-types';

import { ObjectType, TitleFilter } from '@/@types/graphql-types';
import { ScoringProvider } from './scoring-helper';

// UTILITY TYPES
type ExtractTypenames<T> = T extends { __typename?: infer name } ? name : never;

/**
 * Extract from a generated graphql type the object types you want based on the "__typename".
 * **This is only a type utility, does not protect you if the value is undefined.**
 *
 * @example
 * ```ts
 * type Node = typeof url.node;
 * (data.node as AssertByTypeName<Node, 'Movie' | 'Show'>).offers;
 * ```
 */
export type AssertByTypename<
	T extends { __typename?: string } | undefined | null,
	Names extends ExtractTypenames<T>
> = T & { __typename: Names };

function getMinMaxFilter(min: any, max: any, property: string) {
	if (!min && !max) {
		return {};
	}

	return {
		[property]: {
			...(min ? { min } : {}),
			...(max ? { max } : {}),
		},
	};
}

//TODO [GraphQL] Workaround to make filters work while we have different filter between filter store (rest api) and graphql
export function toGraphqlFilters(
	filters: FilterCollection,
	providers: string[],
	excludeIrrelevantTitles = false
): TitleFilter {
	const { sort_by, sort_asc, genres, ...restFilters } = filters;
	void sort_by;
	void sort_asc;

	const restToGraphqlMapper: Partial<Record<keyof FilterCollection, keyof TitleFilter>> = {
		age_certifications: 'ageCertifications',
		exclude_genres: 'excludeGenres',
		exclude_production_countries: 'excludeProductionCountries',
		content_types: 'objectTypes',
		production_countries: 'productionCountries',
		subgenres: 'subgenres',
	};

	const restKeys = Object.keys(restToGraphqlMapper) as (keyof FilterCollection)[];
	const graphqlFilters: TitleFilter = restKeys.reduce((graphqlFilters, restKey) => {
		return {
			...graphqlFilters,
			[`${restToGraphqlMapper[restKey]}`]: (restFilters as Partial<FilterCollection>)[restKey],
		};
	}, {} as TitleFilter);

	const objectTypesValue = Object.values(ObjectType);

	return {
		...graphqlFilters,
		genres,
		packages: providers,
		excludeIrrelevantTitles,
		objectTypes: restFilters.content_types
			?.map(objectType => objectType.toUpperCase() as ObjectType)
			.filter(objectType => objectTypesValue.includes(objectType)),
		presentationTypes: restFilters.presentation_types.map(p => p.toUpperCase()) as PresentationType[],
		monetizationTypes: restFilters.monetization_types.map(m => m.toUpperCase()) as MonetizationType[],
		...getMinMaxFilter(restFilters.min_price, restFilters.max_price, 'price'),
		...getMinMaxFilter(
			restFilters.scoring_filter_types?.[ScoringProvider.IMDB]?.min_scoring_value,
			restFilters.scoring_filter_types?.[ScoringProvider.IMDB]?.max_scoring_value,
			'imdbScore'
		),
		...getMinMaxFilter(
			restFilters.scoring_filter_types?.[ScoringProvider.IMDB_VOTES]?.min_scoring_value,
			restFilters.scoring_filter_types?.[ScoringProvider.IMDB_VOTES]?.max_scoring_value,
			'imdbVotes'
		),
		...getMinMaxFilter(
			restFilters.scoring_filter_types?.[ScoringProvider.ROTTEN_TOMATOES]?.min_scoring_value,
			restFilters.scoring_filter_types?.[ScoringProvider.ROTTEN_TOMATOES]?.max_scoring_value,
			'tomatoMeter'
		),
		...getMinMaxFilter(restFilters.release_year_from, restFilters.release_year_until, 'releaseYear'),
		...getMinMaxFilter(
			restFilters.min_runtime && restFilters.min_runtime >= 5
				? restFilters.min_runtime - 5
				: restFilters.min_runtime,
			restFilters.max_runtime ? restFilters.max_runtime + 5 : restFilters.max_runtime,
			'runtime'
		),
	};
}
